import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  CreditCard,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Wrench,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { CardsHeader } from "../components/CardsHeader";
import { CardStatusBadge } from "../components/CardStatusBadge";
import { CreateCardModal } from "../components/CreateCardModal";
import { DeletePocketModal } from "../components/DeletePocketModal";
import { EditPocketModal } from "../components/EditPocketModal";
import { LoadPocketModal } from "../components/LoadPocketModal";
import { PocketIcon } from "../components/PocketIcon";
import { WalletMovementsTable } from "../components/WalletMovementsTable";
import { DEFAULT_POCKET_COLOR, resolvePocketName } from "../constants/pockets.constants";
import { useCardOperationsMaintenance } from "../hooks/useCardOperationsMaintenance";
import { PocketsService } from "../services/pocketsService";
import type { PocketTransferType } from "../services/pocketsService";
import type { Card } from "../types/cards-api.types";
import type { Pocket } from "../types/pockets-api.types";
import type { WalletTransactionEntity } from "../types/wallets-api.types";

const formatUSD = (raw: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

const parseAmount = (raw: number | string | null | undefined): number => {
  if (raw == null) return 0;
  if (typeof raw === "number") return raw;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const PocketDetailPage = () => {
  const { t } = useTranslations("cards");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const params = useParams<"id">();
  const pocketId = params.id!;

  const [pocket, setPocket] = useState<Pocket | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [movements, setMovements] = useState<WalletTransactionEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [isLoadingMovements, setIsLoadingMovements] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCreateCardOpen, setIsCreateCardOpen] = useState(false);
  const [loadModalMode, setLoadModalMode] = useState<PocketTransferType | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshingBalance, setIsRefreshingBalance] = useState(false);

  const { movementsFrozen } = useCardOperationsMaintenance(pocket?.organization_id);

  const pocketName = pocket ? resolvePocketName(pocket) : "";
  const color = pocket?.metadata?.color || DEFAULT_POCKET_COLOR;

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_tarjetas") },
      { label: tGlobal("sidebar_menu_pockets"), to: "/app/cards/pockets" },
      pocketName ? { label: pocketName } : { label: "", loading: true },
    ],
    [tGlobal, pocketName],
  );
  useAppShellBreadcrumb(breadcrumb);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await PocketsService.getPocketById(pocketId);
        if (cancelled) return;
        setPocket(response.data);
        setIsLoading(false);
        // Render immediately with the cached balance, then patch it in the
        // background from the live provider read. Not awaited so it never
        // delays first paint.
        PocketsService.getPocketBalance(pocketId)
          .then((balanceRes) => {
            if (cancelled) return;
            const fresh = parseFloat(balanceRes.data.balance.current.balance);
            if (Number.isFinite(fresh)) {
              setPocket((prev) => (prev ? { ...prev, balance: fresh } : prev));
            }
          })
          .catch(() => {
            // Keep the cached balance if the live read fails.
          });
      } catch (err: unknown) {
        if (cancelled) return;
        setError(HttpApiService.getErrorMessage(err) || t("identity_detail_error_load"));
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pocketId, t, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingCards(true);
    (async () => {
      try {
        const response = await PocketsService.getPocketCards(pocketId);
        if (cancelled) return;
        const list = Array.isArray(response.data) ? response.data : [];
        setCards(list);
      } catch {
        if (!cancelled) setCards([]);
      } finally {
        if (!cancelled) setIsLoadingCards(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pocketId, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingMovements(true);
    (async () => {
      try {
        const response = await PocketsService.getPocketTransactions(pocketId, {
          limit: 20,
          skip: 0,
        });
        if (cancelled) return;
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setMovements(list);
      } catch {
        if (!cancelled) setMovements([]);
      } finally {
        if (!cancelled) setIsLoadingMovements(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pocketId, refreshKey]);

  const handleBack = useCallback(() => navigate("/app/cards/pockets"), [navigate]);

  const handleOpenCard = useCallback((cardId: string) => navigate(`/app/cards/cards/${cardId}`), [navigate]);

  const handlePocketUpdated = (next: Pocket) => {
    setPocket(next);
  };

  const handleRefreshBalance = useCallback(async () => {
    setIsRefreshingBalance(true);
    try {
      const res = await PocketsService.getPocketBalance(pocketId);
      const fresh = parseFloat(res.data.balance.current.balance);
      if (Number.isFinite(fresh)) {
        setPocket((prev) => (prev ? { ...prev, balance: fresh } : prev));
      }
    } catch {
      // balance stays as-is if the fetch fails
    } finally {
      setIsRefreshingBalance(false);
    }
  }, [pocketId]);

  const balance = parseAmount(pocket?.balance);
  const monthSpend = pocket?.month_spend_usd ?? 0;

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 p-2">
      <CardsHeader activeTabId="pockets" />

      {/* Wraps instead of overflowing: five actions next to the back link are ~600px, wider than a phone and
          than a laptop's column. The actions take their own line, then wrap among themselves. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("pocket_detail_back_to_pockets")}
        </button>
        {pocket && (
          <div className="flex flex-wrap items-center gap-2">
            <ColocaButton variant="default" size="sm" icon={Pencil} onClick={() => setIsEditOpen(true)}>
              {t("pocket_edit_button")}
            </ColocaButton>
            <ColocaButton variant="default" size="sm" icon={Trash2} onClick={() => setIsDeleteOpen(true)}>
              {t("pocket_delete_button")}
            </ColocaButton>
            <ColocaButton
              variant="default"
              size="sm"
              icon={CreditCard}
              onClick={() => setIsCreateCardOpen(true)}
              disabled={movementsFrozen}
            >
              {t("pocket_detail_create_card")}
            </ColocaButton>
            <ColocaButton
              variant="default"
              size="sm"
              icon={ArrowRight}
              onClick={() => setLoadModalMode("withdrawal")}
              disabled={movementsFrozen}
            >
              {t("pocket_detail_transfer")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={() => setLoadModalMode("topup")}
              disabled={movementsFrozen}
            >
              {t("pocket_detail_load_balance")}
            </ColocaButton>
          </div>
        )}
      </div>

      {movementsFrozen && (
        <ColocaAlert
          icon={Wrench}
          variant="warning"
          heading={t("card_maintenance_banner_title")}
          text={t("card_maintenance_banner_description")}
        />
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {pocket && !isLoading && (
        <>
          <section
            className="relative overflow-hidden rounded-2xl p-6 text-white shadow-sm"
            style={{
              background: `linear-gradient(120deg, ${color}cc 0%, ${color} 60%, ${color}f0 100%)`,
            }}
          >
            <div className="relative flex flex-wrap items-start justify-between gap-6">
              <div className="flex items-start gap-4">
                <PocketIcon icon={pocket.metadata?.icon} color="rgba(255,255,255,0.18)" size={56} />
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-white/75">
                    {t("pocket_detail_hero_balance_label")}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-3xl font-semibold tracking-tight">
                      {formatUSD(balance)} <span className="text-base font-medium text-white/70">USD</span>
                    </span>
                    <ColocaIconButton
                      icon={RefreshCw}
                      size="xs"
                      variant="ghost"
                      loading={isRefreshingBalance}
                      onClick={handleRefreshBalance}
                      aria-label={t("balance_refresh_btn")}
                      className="text-white/70 hover:bg-white/15 hover:text-white"
                    />
                  </div>
                  <div className="mt-1 text-xs text-white/75">
                    {pocketName} ·{" "}
                    {t("pocket_detail_hero_shared_with", {
                      count: pocket.cards_count ?? cards.length,
                    })}
                  </div>
                </div>
              </div>

              <div className="grid min-w-[280px] grid-cols-2 gap-3">
                <div className="rounded-md border border-white/15 bg-white/[0.12] p-3 backdrop-blur-sm">
                  <div className="text-[10px] uppercase tracking-wider text-white/70">
                    {t("pocket_detail_hero_outflow")}
                  </div>
                  <div className="mt-1 text-xl font-semibold">−{formatUSD(monthSpend)}</div>
                </div>
                <div className="rounded-md border border-white/15 bg-white/[0.12] p-3 backdrop-blur-sm">
                  <div className="text-[10px] uppercase tracking-wider text-white/70">
                    {t("pocket_detail_hero_cards")}
                  </div>
                  <div className="mt-1 text-xl font-semibold">{pocket.cards_count ?? cards.length}</div>
                </div>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="overflow-hidden rounded-2xl border border-border bg-card lg:col-span-1">
              <div className="flex items-center justify-between border-b border-border p-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("pocket_detail_section_cards_title")}
                </h3>
                <span className="text-[11px] text-muted-foreground">{cards.length}</span>
              </div>
              {isLoadingCards ? (
                <div className="flex items-center justify-center p-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : cards.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {t("pocket_detail_section_cards_empty")}
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {cards.map((card) => {
                    const last4 = card.last4 || card.last_4 || "0000";
                    const alias = card.alias?.trim() || `•••• ${last4}`;
                    return (
                      <li key={card.id}>
                        <button
                          type="button"
                          onClick={() => handleOpenCard(card.id)}
                          className="flex w-full items-center gap-3 p-3.5 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                        >
                          <div
                            className="relative h-9 w-14 shrink-0 overflow-hidden rounded text-white"
                            style={{
                              background:
                                "linear-gradient(135deg, var(--color-brand-700), var(--color-brand-950))",
                            }}
                          >
                            <div className="absolute inset-0 flex items-end p-1.5 font-mono text-[8px] text-white/90">
                              ··{last4}
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-foreground">{alias}</div>
                            {card.card_holder_name && (
                              <div className="truncate text-[11px] text-muted-foreground">
                                {card.card_holder_name}
                              </div>
                            )}
                          </div>
                          <CardStatusBadge status={card.status} />
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden="true"
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="overflow-hidden rounded-2xl border border-border bg-card lg:col-span-2">
              <div className="flex items-center justify-between border-b border-border p-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("pocket_detail_section_movements_title")}
                </h3>
                <span className="text-[11px] text-muted-foreground">{movements.length}</span>
              </div>
              {isLoadingMovements ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : movements.length === 0 ? (
                <div className="p-6">
                  <ColocaEmpty
                    title={t("pocket_detail_movements_empty_title")}
                    description={t("pocket_detail_movements_empty_description")}
                  />
                </div>
              ) : (
                <WalletMovementsTable movements={movements} />
              )}
            </div>
          </section>
        </>
      )}

      <EditPocketModal
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        pocket={pocket}
        onUpdated={handlePocketUpdated}
      />

      <DeletePocketModal
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        pocket={pocket}
        onDeleted={handleBack}
      />

      <CreateCardModal
        open={isCreateCardOpen}
        onOpenChange={setIsCreateCardOpen}
        presetPocketId={pocketId}
        lockPocket
        onRefresh={() => setRefreshKey((k) => k + 1)}
      />

      <LoadPocketModal
        open={loadModalMode !== null && !movementsFrozen}
        onOpenChange={(next) => {
          if (!next) setLoadModalMode(null);
        }}
        pocket={pocket}
        initialMode={loadModalMode ?? "topup"}
        onSuccess={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
};
